import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Award, Heart, Leaf, Users, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import PageHero from "../components/PageHero";
import SectionTitle from "../components/SectionTitle";
import { club, images, historyTimeline, purpose, membership, yearsOfTradition } from "../mock/mock";

const values = [
  { icon: Heart, title: "Tradition", text: "Seit 1881 leben wir die Leidenschaft fürs Angeln über Generationen hinweg." },
  { icon: Leaf, title: "Naturschutz", text: "Wir pflegen und schützen unsere Gewässer und die heimische Tierwelt." },
  { icon: Users, title: "Gemeinschaft", text: "Ein herzlicher Verein, in dem Zusammenhalt und Freundschaft zählen." },
  { icon: Award, title: "Verantwortung", text: "Waidgerechtes Angeln und Wissensvermittlung sind uns wichtig." },
];

export default function Verein() {
  return (
    <div>
      <PageHero
        eyebrow="Über uns"
        title="Der Verein"
        subtitle="Ein Stück Fürther Geschichte – geprägt von Leidenschaft, Natur und Gemeinschaft seit 1881."
        image={images.anglerMountain}
      />

      {/* Intro */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="inline-block text-accent text-sm font-600 uppercase tracking-[0.25em] mb-3">
              Wer wir sind
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-600 text-foreground leading-tight">
              Über {yearsOfTradition} Jahre gelebte Fischereitradition
            </h2>
            <div className="mt-6 space-y-4 text-lg text-muted-foreground font-300 leading-relaxed">
              <p>
                Der Fischerei Verein Fürth e.V. wurde 1881 gegründet und gehört
                damit zu den traditionsreichsten Angelvereinen der Region. Bei
                uns treffen sich Menschen jeden Alters, die die Liebe zur Natur
                und zum Angeln verbindet.
              </p>
              <p>
                Unser Vereinsheim in der Amtsgasse 2 in Fürth/Vach ist an
                ausgewählten Montagen geöffnet – hier tauschen wir uns aus, geben
                Karten aus und pflegen die Gemeinschaft.
              </p>
              <p>
                Bei Fragen zur Aufnahme, zu Beiträgen oder zum Vereinsleben
                wendet euch gerne an unseren ersten Vorsitzenden
                {" "}<strong className="text-foreground">{club.chairman}</strong>.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={`mailto:${club.emails.vorstand}`}>
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-500 gap-2">
                  <Mail className="h-4 w-4" /> Vorstand kontaktieren
                </Button>
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={images.waldsee} alt="Waldsee" className="rounded-2xl object-cover h-64 w-full shadow-lg mt-8" />
            <img src={images.altmuehl} alt="Altmühl" className="rounded-2xl object-cover h-64 w-full shadow-lg" />
            <img src={images.woernitz} alt="Wörnitz" className="rounded-2xl object-cover h-64 w-full shadow-lg" />
            <img src={images.rastsee} alt="Rastsee" className="rounded-2xl object-cover h-64 w-full shadow-lg mt-[-2rem]" />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionTitle eyebrow="Unsere Werte" title="Wofür wir stehen" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="bg-card rounded-2xl p-7 border border-border text-center hover:shadow-lg transition-shadow">
                  <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
                    <Icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-serif text-2xl font-600 text-foreground mb-2">{v.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Zweck & Aufgaben */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionTitle
            eyebrow="Unser Auftrag"
            title="Zweck & Aufgaben"
            description={purpose.intro}
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {purpose.goals.map((g, i) => (
              <div
                key={g.title}
                className="bg-card rounded-2xl p-7 border border-border hover:shadow-lg hover:border-primary/30 transition-all"
              >
                <span className="font-serif text-3xl font-700 text-primary/25">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-serif text-2xl font-600 text-foreground mt-2 mb-2">
                  {g.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {g.text}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-muted-foreground italic text-center max-w-2xl mx-auto">
            Der Verein ist gemeinnützig, im Vereinsregister des Amtsgerichts Fürth
            eingetragen und verfolgt weder politische noch konfessionelle Ziele.
          </p>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-background">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <SectionTitle eyebrow="Historie" title="Meilensteine unserer Geschichte" />
          <div className="relative border-l-2 border-primary/20 ml-3 space-y-10">
            {historyTimeline.map((t) => (
              <div key={t.year} className="relative pl-8">
                <span className="absolute -left-[9px] top-1.5 h-4 w-4 rounded-full bg-accent border-4 border-background" />
                <div className="font-serif text-3xl font-700 text-primary">{t.year}</div>
                <h3 className="font-600 text-lg text-foreground mt-1">{t.title}</h3>
                <p className="text-muted-foreground mt-1 leading-relaxed">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mitgliedschaft */}
      <section className="py-24 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionTitle
            eyebrow="Mitmachen"
            title="Formen der Mitgliedschaft"
            description={membership.intro}
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {membership.types.map((m) => (
              <div
                key={m.title}
                className="bg-card rounded-2xl p-7 border border-border hover:shadow-lg transition-shadow"
              >
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-serif text-xl font-600 text-foreground mb-2">
                  {m.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {m.text}
                </p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/mitglied-werden">
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-600 gap-2">
                <Mail className="h-4 w-4" /> Mitglied werden – jetzt anfragen
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <MapPin className="h-10 w-10 text-accent mx-auto mb-5" />
          <h2 className="font-serif text-3xl md:text-4xl font-600">Besucht uns im Vereinsheim</h2>
          <p className="mt-3 text-lg text-primary-foreground/80">
            {club.address.heim}, {club.address.city}
          </p>
          <Link to="/termine" className="inline-block mt-7">
            <Button className="bg-accent hover:bg-accent/90 text-accent-foreground font-600 gap-2">
              Öffnungstermine ansehen <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
